import { FC, useEffect, useState } from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import cn from './index.module.css'
import Modal from '@common/Modal'
import classNames from 'classnames'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import { useDispatch, useSelector } from 'react-redux'
import { actionUpdProfile, selectProfile } from '@store/profile.reducer'
import PrimaryButton from '@pages/Admin/CourseSettings/components/PrimaryButton/PrimaryButton'
import { getEmailStatus, sendEmailCode, verifyEmailCode } from './utils/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { checkIsEmail } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/utils/validation'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import { IcoRemove } from '@/utils/icons'

interface IRequiredEmailConfirmationModal {
  open: boolean
  /** Админ может закрыть, чтобы не потерять доступ к платформе */
  canDismiss?: boolean
  onClose?: () => void
  onSuccess?: () => void
}

type VerificationStep = 'send-code' | 'confirm-code'

const CODE_LENGTH = 6

const ModalBody: FC<IRequiredEmailConfirmationModal> = (props) => {
  const [step, setStep] = useState<VerificationStep>('send-code')
  const [pendingEmail, setPendingEmail] = useState('')
  const canDismiss = Boolean(props.canDismiss)

  useEffect(() => {
    if (!props.open) {
      setStep('send-code')
      setPendingEmail('')
    }
  }, [props.open])

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Подтвердите свой email
          </span>
        </div>
        {canDismiss && (
          <div className={mclasses.closeIcon} onClick={() => props.onClose?.()}>
            <IcoRemove />
          </div>
        )}
      </div>
      {canDismiss && (
        <p className={cn.adminDismissHint}>
          Как администратор вы можете закрыть окно и продолжить работу.
        </p>
      )}
      {step === 'send-code' && (
        <SendCodeStep
          onSuccess={(email) => {
            setPendingEmail(email)
            setStep('confirm-code')
          }}
        />
      )}
      {step === 'confirm-code' && (
        <ConfirmCodeStep
          email={pendingEmail}
          onSuccess={() => props?.onSuccess?.()}
          onBack={() => setStep('send-code')}
        />
      )}
    </div>
  )
}

const SendCodeStep = ({
  onSuccess,
}: {
  onSuccess: (email: string) => void
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const [email, setEmail] = useState(String(profile?.email ?? ''))
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    setEmail(String(profile?.email ?? ''))
  }, [profile?.email])

  const onSendEmail = async () => {
    if (isLoading) return
    const normalizedEmail = email.trim()
    if (!checkIsEmail(normalizedEmail) || !profile?.id) return

    setLoading(true)
    try {
      const res = await sendEmailCode(profile.id, normalizedEmail)

      if (res.success) {
        addAlert({
          title: `Код отправлен на ${normalizedEmail}`,
          text: 'Введите 6-значный код из письма. Код действует 10 минут.',
        })
        onSuccess(normalizedEmail)
        return
      }

      addAlert({
        title: 'Ошибка',
        text: res.error || 'Не удалось отправить код подтверждения',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className={mclasses.formWrapper}>
        <TextField
          label="Email"
          description="На этот адрес придёт код подтверждения. Можно указать другой email — после подтверждения он сохранится в профиле."
          value={email}
          onChange={setEmail}
          disabled={isLoading}
        />
      </div>
      <div className={mclasses.actions}>
        <PrimaryButton
          onClick={onSendEmail}
          disabled={isLoading || !checkIsEmail(email.trim())}
          isLoading={isLoading}
        >
          Отправить код
        </PrimaryButton>
      </div>
    </>
  )
}

const ConfirmCodeStep = ({
  email,
  onBack,
  onSuccess,
}: {
  email: string
  onBack: () => void
  onSuccess: () => void
}) => {
  const addAlert = useAddAlert()
  const dispatch = useDispatch()
  const profile = useSelector(selectProfile)
  const [code, setCode] = useState('')
  const [isLoading, setLoading] = useState(false)

  const onConfirm = async () => {
    if (isLoading || !profile?.id) return
    const normalizedCode = code.trim()
    if (normalizedCode.length !== CODE_LENGTH) return

    setLoading(true)
    try {
      const res = await verifyEmailCode(profile.id, normalizedCode)

      if (!res.success) {
        addAlert({
          title: 'Ошибка',
          text: res.error || 'Неверный код или срок действия истёк',
          type: 'error',
        })
        return
      }

      let nextEmail = email
      let confirmed = true
      const status = await getEmailStatus(profile.id)
      if (status.success && status.data) {
        if (typeof status.data.email === 'string' && status.data.email.trim()) {
          nextEmail = status.data.email.trim()
        }
        if (status.data.isEmailConfirmed != null) {
          confirmed = Boolean(status.data.isEmailConfirmed)
        }
      }

      dispatch(
        actionUpdProfile({
          email: nextEmail,
          is_email_confirmed: confirmed ? 1 : 0,
          isEmailConfirmed: confirmed ? 1 : 0,
          requireEmailConfirmation: false,
        }),
      )

      addAlert({
        title: 'Успех',
        text: res.data?.message || 'Почта подтверждена',
      })
      onSuccess()
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className={mclasses.formWrapper}>
        <TextField
          label="Код из письма"
          description={
            email
              ? `Введите 6-значный код, отправленный на ${email}. Код действует 10 минут.`
              : 'Введите 6-значный код из письма. Код действует 10 минут.'
          }
          value={code}
          onChange={(value: string) =>
            setCode(String(value).replace(/\D/g, '').slice(0, CODE_LENGTH))
          }
          disabled={isLoading}
        />
      </div>
      <div className={mclasses.actions}>
        <SecondaryButton onClick={onBack} disabled={isLoading}>
          Назад
        </SecondaryButton>
        <PrimaryButton
          onClick={onConfirm}
          disabled={isLoading || code.trim().length !== CODE_LENGTH}
          isLoading={isLoading}
        >
          Подтвердить
        </PrimaryButton>
      </div>
    </>
  )
}

const RequiredEmailConfirmationModal: FC<IRequiredEmailConfirmationModal> = (
  props,
) => {
  const { open, canDismiss, onClose } = props

  return (
    <Modal
      active={open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      // Закрытие только крестиком у админа — клик по фону оставляем заблокированным,
      // чтобы модалка не исчезала сразу после открытия.
      setActive={(next?: boolean) => {
        if (canDismiss && next === false) onClose?.()
      }}
      disableBackdropDismiss
    >
      <ModalBody {...props} />
    </Modal>
  )
}

export default RequiredEmailConfirmationModal
